'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import TextButtonDropdown from '../TextButtonDropdown/TextButtonDropdown.js';
import TextFieldContainer from '../TextFieldContainer/TextFieldContainer.js';
import Input from '../Input/Input.js';
import '../Input/PasswordInput.js';
import '../Input/LableInput.js';
import '../Input/InputTypeSelector.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * 검색 필드 컴포넌트
 *
 * @description
 * 검색 기능을 위한 입력 필드를 제공합니다. 검색 아이콘과 선택적 필터 드롭다운을 포함합니다.
 *
 * @component
 *
 * @param {Object} props
 * @param {Function} props.onSearch - 검색 실행 시 호출되는 콜백 함수
 * @param {TextButtonDropdownProps} [props.filters] - 필터 드롭다운 설정 (선택사항)
 * @param {boolean} [props.disabled] - 비활성화 상태 여부
 * @param {boolean} [props.spellCheck] - 맞춤법 검사 활성화 여부
 * @param {string} [props.className] - 추가 CSS 클래스
 * @param {Function} [props.onFocus] - 포커스 시 호출되는 콜백 함수
 * @param {Function} [props.onBlur] - 블러 시 호출되는 콜백 함수
 *
 * @example
 * // 기본 사용
 * <SearchField
 *   onSearch={() => console.log('검색 실행')}
 *   placeholder="검색어를 입력하세요"
 * />
 *
 * // 필터가 있는 검색 필드
 * <SearchField
 *   onSearch={() => console.log('검색 실행')}
 *   filters={{
 *     label: "필터",
 *     items: [
 *       { label: "전체", value: "all" },
 *       { label: "제목", value: "title" }
 *     ]
 *   }}
 * />
 */ const SearchField = (_param)=>{
    var { type, onSearch, className, disabled, spellCheck, onFocus, onBlur, filters } = _param, rest = _object_without_properties(_param, [
        "type",
        "onSearch",
        "className",
        "disabled",
        "spellCheck",
        "onFocus",
        "onBlur",
        "filters"
    ]);
    const [isFocused, setIsFocused] = useState(false);
    const onFieldFocus = (e)=>{
        setIsFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    const onFieldBlur = (e)=>{
        setIsFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    };
    const handleSubmit = (e)=>{
        e.preventDefault();
        onSearch();
    };
    return /*#__PURE__*/ jsx("form", {
        onSubmit: handleSubmit,
        "data-testid": "design-system-search-field",
        children: /*#__PURE__*/ jsx("label", {
            className: "cursor-text",
            children: /*#__PURE__*/ jsxs(TextFieldContainer, {
                isFocused: isFocused,
                isDisabled: disabled,
                className: "overflow-visible",
                children: [
                    !!filters && /*#__PURE__*/ jsx(Box, {
                        className: "shrink-0",
                        children: /*#__PURE__*/ jsx(TextButtonDropdown, _object_spread({
                            disabled: disabled
                        }, filters))
                    }),
                    /*#__PURE__*/ jsx(Input, _object_spread({
                        onFocus: onFieldFocus,
                        onBlur: onFieldBlur,
                        spellCheck: spellCheck || false,
                        disabled: disabled
                    }, rest)),
                    /*#__PURE__*/ jsx("button", {
                        type: "submit",
                        className: "shrink-0",
                        "data-testid": "design-system-search-field--submit-button",
                        disabled: disabled,
                        children: /*#__PURE__*/ jsx(SystemIcon, {
                            testId: "design-system-search-field--submit-button-icon",
                            name: "medium_search",
                            className: twMerge('pointer-events-none text-w-gray-600', disabled && 'text-w-gray-300')
                        })
                    })
                ]
            })
        })
    });
};

export { SearchField as default };
